import { DateInput } from "@/common/components/DateInput/DateInput";
import { ErrorMessage } from "@/common/components/ErrorMessage";
import { FileUploader } from "@/common/components/FileUploader";
import Modal from "@/common/components/Modal";
import ResumeBox from "@/common/components/ResumeBox";
import useBlobs from "@/common/hooks/queries/useBlobs";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { formatDateInLocalTimezone } from "@/common/utils/formatters";
import { FormTransformers } from "@/common/utils/formTransformers";
import { getFilenameFromUrl } from "@/common/utils/getFilenameFromUrl";
import { maintenanceRequiredDocumentSchema, MaintenanceRequiredDocumentValidationSchema } from "@/features/establishmentLeadsFunnel/schemas/update.schema";
import { useUpdateEstablishmentCompanyMaintenanceRequiredDocuments } from "@/features/establishments/hooks/maintenanceModels/useUpdateEstablishmentCompanyMaintenanceRequiredDocuments";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

interface UpdateMaintenanceRequiredDocumentProps {
  establishmentCompany?: EstablishmentCompanyData;
  maintenanceRequiredDocument: EstablishmentCompanyData['maintenance_required_documents'][number];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const UpdateMaintenanceRequiredDocument = ({
   establishmentCompany,
    maintenanceRequiredDocument,
    isOpen,
    setIsOpen,
  }: UpdateMaintenanceRequiredDocumentProps) => {
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm<MaintenanceRequiredDocumentValidationSchema>({
    resolver: zodResolver(maintenanceRequiredDocumentSchema),
    defaultValues: {
      id: maintenanceRequiredDocument.id,
      document: {
        id: maintenanceRequiredDocument.document_id,
        display_name: maintenanceRequiredDocument.document.display_name,
      },
      file_attachment_attributes: {
        file_name: maintenanceRequiredDocument.file
          ? getFilenameFromUrl(maintenanceRequiredDocument.file?.url)
          : undefined,
        file_url: maintenanceRequiredDocument.file?.url,
      },
      valid_until: maintenanceRequiredDocument.valid_until
        ? formatDateInLocalTimezone(maintenanceRequiredDocument.valid_until)
        : undefined,
      issue_date: maintenanceRequiredDocument.issue_date
        ? formatDateInLocalTimezone(maintenanceRequiredDocument.issue_date)
        : undefined,
    }
  });

  const {
    mutateAsync: uploadBlob,
    isPending: uploadBlobIsPending,
  } = useBlobs();

  const {
    mutate: updateMaintenanceRequiredDocument,
    isPending: updateMaintenanceRequiredDocumentIsPending,
  } = useUpdateEstablishmentCompanyMaintenanceRequiredDocuments({
    establishmentCompanyId: establishmentCompany?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishmentCompany?.id] });
      setIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({
        setError,
      })(error);
    },
  });

  const onSubmit: SubmitHandler<MaintenanceRequiredDocumentValidationSchema> = (data) => updateMaintenanceRequiredDocument(data);

  const resetForm = () => {
    reset({
      id: maintenanceRequiredDocument.id,
      document: {
        id: maintenanceRequiredDocument.document_id,
        display_name: maintenanceRequiredDocument.document.display_name,
      },
      file_attachment_attributes: {
        file_name: maintenanceRequiredDocument.file
          ? getFilenameFromUrl(maintenanceRequiredDocument.file?.url)
          : undefined,
        file_url: maintenanceRequiredDocument.file?.url,
      },
      valid_until: maintenanceRequiredDocument.valid_until
        ? formatDateInLocalTimezone(maintenanceRequiredDocument.valid_until)
        : undefined,
      issue_date: maintenanceRequiredDocument.issue_date
        ? formatDateInLocalTimezone(maintenanceRequiredDocument.issue_date)
        : undefined,
    });
  }

  const handleUploadDocument = async (file: File) => {
    const blob = await uploadBlob({ file, filename: file.name });

    const blobId = blob?.data?.data?.id;

    setValue(`file_attachment_attributes.blob_id`, blobId);

    setValue(`file_attachment_attributes.file_url`, URL.createObjectURL(file));

    setValue(`file_attachment_attributes.file_name`, file.name);
  }

  useEffect(() => {
    if (!isOpen) {
      setTimeout(resetForm, 500);
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        title="Editar Documento do estabelecimento"
        useCancellableFooter
        cancelButtonClassName="bg-white-200 rounded-es-lg"
        confirmButtonClassName="bg-blue-700 rounded-ee-lg"
        confirmButtonIsLoading={updateMaintenanceRequiredDocumentIsPending}
        onConfirm={() => {
          const hasDocument = !!watch('document');

          if (hasDocument) {
            handleSubmit(onSubmit)();
          } else {
            setIsOpen(false);
          }
        }}
        body={(
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 text-white-800">
            <div className="flex flex-col gap-3 bg-white-100 p-3 rounded-lg lg:bg-transparent lg:p-0 lg:rounded-none">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-4">
                  <ResumeBox
                    label="Nome do documento"
                    value={watch('document').display_name}
                    padding="small"
                    boxClassName="bg-white-200"
                    labelClassName="text-sm text-[11px]"
                    valueClassName="text-sm font-bold"
                  />

                  <div className="flex flex-col xl:flex-row gap-3">
                    <DateInput
                      label="Data de emissão"
                      control={control}
                      fieldName={`issue_date`}
                      errorMessage={errors?.issue_date?.message || ''}
                      disabled={updateMaintenanceRequiredDocumentIsPending}
                      value={watch('issue_date')}
                      className="flex-1"
                    />

                    <DateInput
                      label="Validade do documento"
                      control={control}
                      fieldName={`valid_until`}
                      errorMessage={errors?.valid_until?.message || ''}
                      disabled={updateMaintenanceRequiredDocumentIsPending}
                      value={watch('valid_until')}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <FileUploader
                    title="Anexe documentos"
                    className="min-h-[108px] w-full bg-yellow-op-2 border-none"
                    accept=".png, .jpg, .webp, .pdf"
                    handleChange={handleUploadDocument}
                    disabled={updateMaintenanceRequiredDocumentIsPending || uploadBlobIsPending}
                    isLoading={uploadBlobIsPending}
                    maxFileSizeInMb={20}
                    currentFileUrl={watch('file_attachment_attributes')?.file_url}
                    fileName={watch('file_attachment_attributes')?.file_name}
                  />

                  {errors?.file_attachment_attributes?.file_name?.message && (
                    <ErrorMessage
                      name={`file_attachment_attributes.file_name`}
                      message={errors?.file_attachment_attributes?.file_name.message}
                    />
                  )}
                </div>
              </div>
            </div>
          </form >
        )}
      />
    </>
  );
}

export default UpdateMaintenanceRequiredDocument;
